import { type PowerSyncDrizzleDB, user } from "@fieldcheck/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * ── Por qué TODAS las mutaciones de abajo llevan `.where(eq(user.id, userId))` ─
 *
 * Cuatro de ellas (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) tenían esta forma:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * es decir, en modo invitado el UPDATE salía **sin `WHERE`** → reescribía TODAS
 * las filas de `user` de la base local.
 *
 * La premisa de la que colgaba («la base del invitado solo tiene una fila
 * `user`») es falsa en una ventana concreta: en el upgrade invitado→real la
 * base del invitado se CONECTA con el conector de la cuenta real y se
 * auto-suscribe a todos los streams — incluido `organizations`, que sincroniza
 * una fila de `user` por CADA co-miembro de las organizaciones de esa cuenta.
 * Ahí la tabla tiene 1 + 1 + N filas, `user` NO es `localOnly`, y cada fila
 * tocada encola su propio op en `ps_crud`.
 *
 * El filtro correcto NO es adivinado: la fila del invitado la siembra
 * `ThemeProvider` (`apps/web/src/providers/theme-provider.tsx`) con
 * `db.insert(user).values({ id: uid, … })`, y para una pestaña invitada `uid`
 * es exactamente `VISITOR_ID`. Así que `eq(user.id, userId)` apunta a la MISMA
 * fila que la rama sin filtro pretendía tocar cuando la tabla tenía una sola
 * fila — comportamiento idéntico en el caso normal, acotado en el resto. Es
 * además la forma que ya usaban `updateUserFontFamilyMutation` y
 * `updateUserImageMutation`, que nunca tuvieron rama de invitado.
 *
 * Guardado por `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 *
 * ── §4.11: `user` NO se excluye del blindaje de escritura verificada ────────
 * (actualizado tras el guard de siembra de `ThemeProvider`, mismo día) ──────
 *
 * `infra/guides/POWERSYNC_VERIFIED_WRITE.md` §4.1 audita 11 repos que excluían
 * `user` del `UPDATE` fantasma dando por sentado que «el servidor ya creó la
 * fila antes de que el cliente escriba, así que siempre está sincronizada»: en
 * los 11 la exclusión estaba MAL, empezando por el propio canon. Se verificó
 * aquí con los 3 comandos que exige esa sección:
 *
 *   git grep -n '\.insert(user' -- '*.ts' '*.tsx' | grep -v __tests__
 *     → SOLO `apps/web/src/providers/theme-provider.tsx:158`.
 *   git grep -rn 'insert(user' apps/web/src/**\/theme* apps/web/src/**\/*provider*
 *     → el mismo sitio.
 *   git grep -n 'ensureUser\|ensureUserSettingsRow'
 *     → CERO resultados: no hay red de seguridad transaccional.
 *
 * Estos dos hechos, por sí solos, ya abren la ventana sin necesidad de ningún
 * insert local:
 *
 *   - `db` (accessor de PowerSync) se vuelve no-nulo en cuanto se abre el
 *     SQLite local, no cuando termina el sync inicial.
 *   - `userId()` se activa en cuanto `signUpActor`/`signInActor` reciben la
 *     sesión del servidor (`packages/shared/src/auth/controllers.ts`) — una
 *     llamada REST independiente del stream de PowerSync.
 *
 * Entre que `db`+`userId()` quedan listos y que el checkpoint inicial trae la
 * fila `user` que el servidor crea al registrarse, hay una ventana en la que
 * un `UPDATE` normal (idioma, tema…) cae contra una fila que la vista local
 * todavía no tiene — casa CERO filas y devuelve «éxito» en silencio.
 *
 * (Contexto histórico, ya CERRADO) Hasta el guard de arriba, un tercer factor
 * agravaba esa misma ventana para cuentas reales: el `createEffect` de
 * `ThemeProvider` (`apps/web/src/providers/theme-provider.tsx`) insertaba
 * localmente una fila `user` fantasma de 3 columnas para CUALQUIER `uid` — su
 * guard era solo `if (!db || !uid) return`, sin distinguir invitado de cuenta
 * real ni esperar `status().hasSynced` — compitiendo con la fila real que el
 * servidor ya había creado (`packages/core-rust/src/db/user.rs:101,350`,
 * `INSERT INTO "user"`) y que estaba de camino por sync. El guard
 * `uid !== VISITOR_ID` de `theme-provider.tsx` cerró esa causa raíz concreta.
 *
 * **Esto NO vuelve innecesaria la protección de `patchRowVerified` sobre
 * `user`**: los dos puntos de arriba (`db` y `userId()` listos antes que el
 * checkpoint) siguen abriendo la misma ventana para cualquier escritura
 * temprana, con o sin fila fantasma de por medio — es defensa en profundidad
 * genérica del checkpoint, no un parche pegado al bug ya cerrado. Si algún día
 * se retira, que sea porque se demuestra que esa ventana de arriba ya no
 * existe, no porque `ThemeProvider` dejó de sembrar fantasmas.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Escribe la CLAVE del avatar en la fila local.
 *
 * Se escribe local-first (igual que idioma, fuente y tema) y no por REST: así
 * la foto nueva se ve al instante y sobrevive a una recarga en frío sin
 * conexión. El BINARIO sí va por REST (`POST /api/avatar`) porque no cabe en la
 * cola de sync; lo que viaja por aquí es solo la clave que devuelve esa subida.
 *
 * `null` = «quitar la foto» (vuelven las iniciales).
 *
 * El invitado no tiene fila propia sincronizable ni puede tener avatar (regla
 * cero), así que aquí no hay rama `VISITOR_ID` como en `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}
